import Alloy from '@/components/assets/alloy.png';
import SalesForce from '@/components/assets/salesforce.png';
import { ApplicantPersona } from '@/components/pages/Applicant/Applicant.types';
import { useCurrentUserEmail } from '@/components/hooks/useCurrentUserEmail';
import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { usePermissions } from '@/components/providers/PermissionsContextProvider';
import { NoMarginParagraph } from '@/components/shared/components/common.StyledComponents';
import { MenuButton } from '@/components/shared/components/MenuButton';
import { Feature, PERMISSION_DENIED_TITLE } from '@/components/shared/constants/features';
import { useRefreshStatusLoadPayMutation } from '@/components/shared/api/mutations/applicant';
import { Config } from '@/components/shared/utils/config';
import { formatPhoneNumber } from '@/components/shared/utils/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, BadgeColors, Button, ButtonVariants, Icon, IconNames, Link } from '@triumph/design-system';
import { useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
    EVENT_LOG_NAMES,
    getLoggingContext,
    honeybadgerLogEvent,
} from '@/components/shared/utils/logging';
import { CopyOrpLinkButton } from '@/components/shared/components/CopyOrpLinkButton';
import {
    ApplicantId,
    BottomSectionWrapper,
    ButtonColumnWrapper,
    ButtonsWrapper,
    ContactInfoWrapper,
    Container,
    EmailPhoneWrapper,
    HeaderWrapper,
    LastRefreshedText,
    MenuButtonWrapper,
    SalesForceButton,
    SalesForceImg,
    StyledButton,
    StyledImg,
    Title,
    TitleColumnWrapper,
} from './ApplicantHeader.StyledComponents';

const renderPhone = (phone?: string) => {
    if (!phone) return <NoMarginParagraph>In Progress...</NoMarginParagraph>;

    return (
        <EmailPhoneWrapper>
            <Icon name={IconNames.Devices_Phone} id="phone-icon" />
            <Link data-testid="business-phone" href={`tel:${phone}`}>
                {formatPhoneNumber(phone)}
            </Link>
        </EmailPhoneWrapper>
    );
};

const renderEmail = (email?: string) => {
    if (!email) return <NoMarginParagraph>In Progress...</NoMarginParagraph>;

    return (
        <EmailPhoneWrapper>
            <Icon name={IconNames.Office_Mail} id="phone-icon" />
            <Link data-testid="business-email" href={`mailto:${email}`}>
                {email}
            </Link>
        </EmailPhoneWrapper>
    );
};

// Header indicator for the applicant's persona. Carrier and Driver each get a
// distinct badge; Unknown/absent personas render nothing.
const PERSONA_BADGE: Partial<Record<ApplicantPersona, { label: string; color: BadgeColors }>> = {
    [ApplicantPersona.Carrier]: { label: 'Carrier', color: BadgeColors.SECONDARY },
    [ApplicantPersona.Driver]: { label: 'Driver', color: BadgeColors.PRIMARY },
};

const formatLastRefreshed = (date: Date): string => {
    return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    });
};

export const ApplicantHeader = ({
    onPrintClick,
    onBeforeRefresh,
    isPrinting,
}: {
    onPrintClick?: () => void;
    onBeforeRefresh?: () => void;
    isPrinting?: boolean;
}) => {
    const { applicant, applicantId } = useApplicantContext();
    const { hasFeature } = usePermissions();
    const queryClient = useQueryClient();
    const { authState } = useOktaAuth();
    const currentUserEmail = useCurrentUserEmail();
    const { mutateAsync: refreshStatusLoadPay } = useRefreshStatusLoadPayMutation();
    const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(false);

    const contactInfo = applicant?.contactInfo;
    const businessInfo = applicant?.businessInfo;
    const personaBadge =
        applicant?.persona !== undefined ? PERSONA_BADGE[applicant.persona] : undefined;

    const salesForceLink: string = `${Config.SalesforceUrl}/apex/OnlineApplicationResolver?appId=${applicantId}`;

    const handleSalesforceClick = () => {
        const userId = authState?.idToken?.claims?.sub;

        honeybadgerLogEvent(EVENT_LOG_NAMES.SalesforceShortcutClick, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
            },
        });
        window.open(salesForceLink, '_blank', 'noopener,noreferrer');
    };

    // Opens application queue for applicant id sorted by the created at column in descending order
    const alloyLink: string = `https://tbk-bank.app.alloy.com/v3/dashboard/journeys/applications/?filter=%7B%7D&numRowsPerPage=20&pageNum=1&search=%7B%22externalApplicationId%22%3A%22${applicantId}%22%7D&sort=%7B%22journeyApplicationCreatedAt%22%3A%22desc%22%7D`;
    const handleAlloyClick = () => {
        const userId = authState?.idToken?.claims?.sub;

        honeybadgerLogEvent(EVENT_LOG_NAMES.AlloyShortcutClick, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
            },
        });
        window.open(alloyLink, '_blank', 'noopener,noreferrer');
    };

    const handleRefresh = async () => {
        onBeforeRefresh?.();
        const userId = authState?.idToken?.claims?.sub;

        honeybadgerLogEvent(EVENT_LOG_NAMES.RefreshClick, {
            level: 'INFO',
            customAttributes: {
                ...getLoggingContext(currentUserEmail, userId),
                currentPage: window.location.pathname,
                timestamp: new Date().toISOString(),
                applicantId,
            },
        });

        setIsRefreshing(true);
        setRefreshError(false);
        try {
            if (applicant?.loadPayProduct) {
                await refreshStatusLoadPay({ applicantId: applicant.id });
            }
            await queryClient.invalidateQueries();
            // Only update timestamp on successful refresh
            setLastRefreshed(new Date());
        } catch (error) {
            console.error('Failed to refresh data:', error);
            setRefreshError(true);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <Container>
            <HeaderWrapper>
                <TitleColumnWrapper>
                    <CopyOrpLinkButton
                        label="📄 Copy Active ORP Link"
                        data-testid="copy-orp-link-button"
                    />
                    <Title data-testid="legal-name">
                        {businessInfo?.legalName || 'In Progress...'}
                    </Title>
                    <ApplicantId small data-testid="applicant-id">
                        {applicantId}
                    </ApplicantId>
                    {personaBadge ? (
                        <Badge
                            data-testid="persona-badge"
                            label={personaBadge.label}
                            color={personaBadge.color}
                        />
                    ) : null}
                </TitleColumnWrapper>
                <ButtonColumnWrapper>
                    <ButtonsWrapper>
                        <Button
                            data-testid="refresh-button"
                            onClick={handleRefresh}
                            disabled={isRefreshing || !hasFeature(Feature.Refresh)}
                            loading={isRefreshing}
                            variant={ButtonVariants.secondary}
                            title={
                                !hasFeature(Feature.Refresh) ? PERMISSION_DENIED_TITLE : undefined
                            }
                        >
                            Refresh
                        </Button>
                        <MenuButtonWrapper>
                            <MenuButton />
                        </MenuButtonWrapper>
                        <StyledButton
                            onClick={handleAlloyClick}
                            variant={ButtonVariants.secondary}
                            data-testid="alloy-url"
                            disabled={!hasFeature(Feature.RedirectAlloy)}
                            title={
                                !hasFeature(Feature.RedirectAlloy)
                                    ? PERMISSION_DENIED_TITLE
                                    : undefined
                            }
                        >
                            <StyledImg src={Alloy} />
                            Alloy
                        </StyledButton>
                        <SalesForceButton
                            onClick={handleSalesforceClick}
                            variant={ButtonVariants.secondary}
                            data-testid="salesforce-button"
                            disabled={!hasFeature(Feature.RedirectSalesforce)}
                            title={
                                !hasFeature(Feature.RedirectSalesforce)
                                    ? PERMISSION_DENIED_TITLE
                                    : undefined
                            }
                        >
                            <SalesForceImg src={SalesForce} />
                            Salesforce
                        </SalesForceButton>
                        <Button
                            variant={ButtonVariants.secondary}
                            icon={IconNames.Office_Print}
                            onClick={onPrintClick}
                            data-testid="print-to-pdf"
                            loading={isPrinting}
                            disabled={isPrinting}
                            width="40px"
                        />
                    </ButtonsWrapper>
                    {!isRefreshing && (
                        <LastRefreshedText
                            data-testid={refreshError ? 'refresh-error' : 'last-refreshed'}
                            isError={refreshError}
                        >
                            {refreshError
                                ? 'Failed to refresh data'
                                : `Last refreshed: ${formatLastRefreshed(lastRefreshed)}`}
                        </LastRefreshedText>
                    )}
                </ButtonColumnWrapper>
            </HeaderWrapper>
            <BottomSectionWrapper>
                <ContactInfoWrapper>
                    {renderPhone(applicant?.phone || contactInfo?.businessPhone)}
                    {renderEmail(applicant?.email || contactInfo?.businessEmail)}
                </ContactInfoWrapper>
            </BottomSectionWrapper>
        </Container>
    );
};
